import React, { useEffect, useRef, useState } from 'react';
import edPointer from './assets/ed-pointer.svg';
import './css/Education.css';

const academicData = [
  {
    id: 'ms',
    period: 'Upcoming (OCT 2026)',
    degree: 'M.Sc. Interactive Media',
    institution: 'University of Applied Sciences Upper Austria',
    location: 'Hagenberg, Austria',
    score: 'Upcoming',
    scoreLabel: 'Status',
    accent: '#00c9a7',
    side: 'right',
    pills: [
      'Hypermedia UX Engineering',
      'Artificial Intelligence',
    ],
  },
  {
    id: 'btech',
    period: 'Aug 2019 – May 2023',
    degree: 'B.Tech — Information Technology',
    institution: 'Maharshi Dayanand University',
    location: 'Rohtak, Haryana, India',
    score: '79.6%',
    scoreLabel: 'Score',
    accent: '#6d56ff',
    side: 'left',
    pills: [
      'Operating Systems',
      'Data Structures',
      'Analysis of Algorithms',
      'Computer Networks',
      'DBMS',
      'Object-Oriented Programming',
    ],
  },
  {
    id: 'class12',
    period: 'May 2017 – Mar 2019',
    degree: 'All India Senior School Certificate — Class XII',
    institution: 'Shri S.N. Sidheshwar Sr. Secondary Public School',
    location: 'Gurgaon, Haryana, India',
    score: '78%',
    scoreLabel: 'Score',
    accent: '#ff4d8d',
    side: 'right',
    pills: [],
  },
  {
    id: 'class10',
    period: 'May 2016 – Mar 2017',
    degree: 'Secondary School Examination — Class X',
    institution: 'Shri S.N. Sidheshwar Sr. Secondary Public School',
    location: 'Gurgaon, Haryana, India',
    score: '10.0 / 10.0',
    scoreLabel: 'Score',
    accent: '#ff7e3b',
    side: 'left',
    pills: [],
  },
];

const achievementsData = [
  {
    category: 'Certifications',
    accent: '#6d56ff',
    icon: '🎓',
    items: [
      'Namaste Frontend System Design — NamasteDev.com (Dec 2025)',
      'Technical Support Fundamentals — Google / Coursera (May 2021)',
      'The Bits and Bytes of Computer Networking — Google / Coursera (Jul 2021)',
    ],
  },
  {
    category: 'Google Cloud',
    accent: '#00c9a7',
    icon: '☁️',
    items: [
      'Create and Manage Cloud Resources (Sep 2020)',
      'Deploy Kubernetes Applications on Google Cloud',
      'Perform Foundational Infrastructure Tasks in Google Cloud',
      'Build and Secure Networks in Google Cloud (Nov 2020)',
      'Set Up and Configure a Cloud Environment in Google Cloud',
    ],
  },
  {
    category: 'Leadership & Competitions',
    accent: '#ff7e3b',
    icon: '🏆',
    items: [
      'Google Developer Student Club Lead — Dronacharya College of Engineering, Gurugram (2021–2022)',
      '30 Days of Google Cloud — Campus Facilitator (Nov 2021)',
      'Winner — IHack by UPES-CSI',
      'Semi-finalist — Azure Women\'s Hackathon 2022',
      'Code Jam to I/O for Women — April 2021',
      'MLH Local Hack Day Build — Jan 2021',
    ],
  },
  {
    category: 'Languages & Interests',
    accent: '#ff4d8d',
    icon: '🌐',
    items: [
      'English — Fluent',
      'German — B1 course in progress',
      'Hindi — Native',
      'Hobbies - Sketching, Painting, Illustration',
    ],
  },
];

function hexToRgb(hex) {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!r) return '255,255,255';
  return `${parseInt(r[1], 16)},${parseInt(r[2], 16)},${parseInt(r[3], 16)}`;
}

const CardContent = ({ ed }) => (
  <>
    <div className="ed-score-badge">
      <span className="ed-score-label">{ed.scoreLabel}</span>
      <span className="ed-score-value">{ed.score}</span>
    </div>
    <span className="ed-period">{ed.period}</span>
    <h4 className="ed-degree">{ed.degree}</h4>
    <p className="ed-institution">{ed.institution}</p>
    <p className="ed-location">{ed.location}</p>
    {ed.pills.length > 0 && (
      <>
        <div className="ed-divider" />
        <ul className="ed-pills">
          {ed.pills.map((p, i) => (
            <li key={i} className="ed-pill">{p}</li>
          ))}
        </ul>
      </>
    )}
  </>
);

const Education = () => {
  const [visible, setVisible] = useState({});
  const [achVisible, setAchVisible] = useState({});
  const rowRefs = useRef([]);
  const achRefs = useRef([]);

  useEffect(() => {
    const observe = (els, setter) =>
      els.map((el, i) => {
        if (!el) return null;
        const obs = new IntersectionObserver(
          ([entry]) => { if (entry.isIntersecting) setter(p => ({ ...p, [i]: true })); },
          { threshold: 0.15 }
        );
        obs.observe(el);
        return obs;
      });

    const r = observe(rowRefs.current, setVisible);
    const a = observe(achRefs.current, setAchVisible);
    return () => { r.forEach(o => o?.disconnect()); a.forEach(o => o?.disconnect()); };
  }, []);

  return (
    <>


      <div className="education">

        <div className="ed-orb ed-orb-1" />
        <div className="ed-orb ed-orb-2" />
        <div className="ed-orb ed-orb-3" />

        <h3 className="ed-ghost-title">Education</h3>

        {/* ── Academic Timeline ── */}
        <div className="ed-timeline">
          {academicData.map((ed, i) => {
            const isLeft = ed.side === 'left';
            const rgb = hexToRgb(ed.accent);
            return (
              <div
                key={ed.id}
                ref={el => (rowRefs.current[i] = el)}
                className={`ed-row ${visible[i] ? 'visible' : ''}`}
                style={{
                  '--ed-accent': ed.accent,
                  '--ed-accent-rgb': rgb,
                  transitionDelay: `${i * 0.12}s`,
                }}
              >
                {isLeft ? (
                  <div className="ed-card ed-left">
                    <CardContent ed={ed} />
                  </div>
                ) : <div className="ed-empty" />}

                <div className="ed-node">
                  <div className="ed-node-dot" />
                </div>

                {!isLeft ? (
                  <div className="ed-card ed-right">

                    <CardContent ed={ed} />
                  </div>
                ) : <div className="ed-empty" />}
              </div>
            );
          })}
        </div>

        {/* ── Achievements ── */}
        <div className="ed-ach-section">
          <p className="ed-ach-label">Certifications · Achievements · Languages</p>
          <div className="ed-ach-grid">
            {achievementsData.map((ach, i) => {
              const rgb = hexToRgb(ach.accent);
              return (
                <div
                  key={ach.category}
                  ref={el => (achRefs.current[i] = el)}
                  className={`ed-ach-card ${achVisible[i] ? 'visible' : ''}`}
                  style={{
                    '--ach-accent': ach.accent,
                    '--ach-accent-rgb': rgb,
                    transitionDelay: `${i * 0.1}s`,
                  }}
                >
                  <div className="ed-ach-header">
                    {/* <span className="ed-ach-icon">{ach.icon}</span> */}
                    <span className="ed-ach-title">{ach.category}</span>
                  </div>
                  <ul className="ed-ach-items">
                    {ach.items.map((item, j) => (
                      <li key={j} className="ed-ach-item">{item}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        <div className='italic'>
          beyond code
          <span>.-  .-.  -</span>
        </div>

      </div>
    </>
  );
};

export default Education;